import JSZip from 'jszip';
import mammoth from 'mammoth';
import BaseConverter from './BaseConverter.js';
import docxCss from '../../styles/docx-conversion.css?raw';

/**
 * Converter for DOCX files to EPUB format
 */
class DocxConverter extends BaseConverter {
  /**
   * Check if a file is a DOCX file
   */
  isDocxFile(fileName) {
    return fileName.toLowerCase().endsWith('.docx');
  }

  /**
   * Convert DOCX file to EPUB
   */
  async convertToEpub(docxFile, options, onFileProgress) {
    console.log('=== DOCX CONVERSION START ===');
    console.log('Converting DOCX file:', docxFile.name);
    
    const epubZip = new JSZip();
    const bookTitle = docxFile.name.replace('.docx', '');
    console.log('Book title:', bookTitle);

    // Create META-INF directory and container.xml
    this.createContainerXml(epubZip);
    
    // Create mimetype file
    this.createMimetypeFile(epubZip);
    
    // Report progress
    if (onFileProgress) onFileProgress(10);

    try {
      // Convert DOCX to HTML using mammoth
      const arrayBuffer = await docxFile.arrayBuffer();
      const result = await mammoth.convertToHtml({ arrayBuffer });
      
      // Report progress
      if (onFileProgress) onFileProgress(50);
      
      // Create OEBPS directory structure for text-based EPUB
      await this.createOebpsStructure(epubZip, bookTitle, result.value, options);
      
      // Report progress
      if (onFileProgress) onFileProgress(90);
      
      // Log any conversion messages/warnings
      if (result.messages && result.messages.length > 0) {
        console.log('DOCX conversion messages:', result.messages);
      }
      
    } catch (error) {
      console.error('Error converting DOCX file:', error);
      throw new Error(`Failed to convert DOCX file: ${error.message}`);
    }

    // Report progress
    if (onFileProgress) onFileProgress(100);

    console.log('DOCX conversion completed successfully');
    console.log('=== DOCX CONVERSION END ===');

    // Generate EPUB as blob
    return await this.generateEpubBlob(epubZip, options.compressionLevel);
  }

  /**
   * Create OEBPS directory structure for DOCX conversion
   */
  async createOebpsStructure(epubZip, bookTitle, htmlContent, options) {
    // Create OEBPS directory
    epubZip.folder('OEBPS');
    
    // Create content.opf
    this.createContentOpf(epubZip, bookTitle);
    
    // Create toc.ncx
    this.createTocNcx(epubZip, bookTitle);
    
    // Create stylesheet
    this.createStylesheet(epubZip);
    
    // Create main content HTML file
    this.createHtmlContent(epubZip, bookTitle, htmlContent);
  }

  /**
   * Create content.opf file for DOCX EPUB
   */
  createContentOpf(epubZip, bookTitle) {
    const contentOpf = `<?xml version="1.0" encoding="UTF-8"?>
<package version="2.0" xmlns="http://www.idpf.org/2007/opf" unique-identifier="BookId">
  <metadata xmlns:dc="http://purl.org/dc/elements/1.1/" xmlns:opf="http://www.idpf.org/2007/opf">
    <dc:title>${bookTitle}</dc:title>
    <dc:creator>FileConvertHub</dc:creator>
    <dc:identifier id="BookId">urn:uuid:${this.generateUUID()}</dc:identifier>
    <dc:language>en</dc:language>
    <meta name="cover" content="cover"/>
  </metadata>
  <manifest>
    <item id="ncx" href="toc.ncx" media-type="application/x-dtbncx+xml"/>
    <item id="stylesheet" href="stylesheet.css" media-type="text/css"/>
    <item id="content" href="content.html" media-type="application/xhtml+xml"/>
  </manifest>
  <spine toc="ncx">
    <itemref idref="content"/>
  </spine>
</package>`;
    
    epubZip.file('OEBPS/content.opf', contentOpf);
  }

  /**
   * Create toc.ncx file for DOCX EPUB
   */
  createTocNcx(epubZip, bookTitle) {
    const tocNcx = `<?xml version="1.0" encoding="UTF-8"?>
<ncx version="2005-1" xmlns="http://www.daisy.org/z3986/2005/ncx/">
  <head>
    <meta name="dtb:uid" content="urn:uuid:${this.generateUUID()}"/>
    <meta name="dtb:depth" content="1"/>
    <meta name="dtb:totalPageCount" content="0"/>
    <meta name="dtb:maxPageNumber" content="0"/>
  </head>
  <docTitle>
    <text>${bookTitle}</text>
  </docTitle>
  <navMap>
    <navPoint id="navpoint-1" playOrder="1">
      <navLabel>
        <text>${bookTitle}</text>
      </navLabel>
      <content src="content.html"/>
    </navPoint>
  </navMap>
</ncx>`;

    epubZip.file('OEBPS/toc.ncx', tocNcx);
  }

  /**
   * Create stylesheet for DOCX EPUB
   */
  createStylesheet(epubZip) {
    epubZip.file('OEBPS/stylesheet.css', docxCss);
  }

  /**
   * Create main HTML content file for DOCX EPUB
   */
  createHtmlContent(epubZip, bookTitle, htmlContent) {
    const htmlDocument = `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.1//EN" "http://www.w3.org/TR/xhtml11/DTD/xhtml11.dtd">
<html xmlns="http://www.w3.org/1999/xhtml">
<head>
    <title>${bookTitle}</title>
    <link rel="stylesheet" type="text/css" href="stylesheet.css"/>
</head>
<body>
    <h1>${bookTitle}</h1>
    ${htmlContent}
</body>
</html>`;

    epubZip.file('OEBPS/content.html', htmlDocument);
  }
}

export default DocxConverter;
